import { useEffect, useMemo, useRef, useState } from "react";
import {
  AreaSeries,
  ColorType,
  createChart,
  CrosshairMode,
  LineSeries,
  LineStyle,
  TickMarkType,
  TrackingModeExitMode,
  type AutoscaleInfoProvider,
  type IChartApi,
  type ISeriesApi,
  type MouseEventParams,
  type SeriesType,
  type Time,
  type UTCTimestamp,
} from "lightweight-charts";
import {
  PORTFOLIO_RANGES,
  type BalanceSplit,
  type PortfolioPoint,
  type PortfolioRange,
  type PortfolioSeries,
  type TradingResults,
} from "@auto-pools/core";
import { api } from "../api/client";
import { formatMoney, formatPercent2, formatSignedAmount, signTone } from "../lib/format";
import {
  chartNote,
  chipStates,
  effectiveRange,
  formatPlotDate,
  hoverLabel,
  intervalLabel,
  plotRows,
  plotTime,
  resultFigure,
  valueEdge,
  youngRecord,
  type ChartMode,
  type ChartPreferences,
  type ChartUnit,
  type ChipState,
  type PlotRow,
  type ResultSeries,
  type ValueSeries,
} from "./portfolioChartState";

/** The chart re-reads its points this often while the panel is open. */
const REFRESH_EVERY_MS = 5 * 60_000;
const RANGE_LABELS: Record<PortfolioRange, string> = {
  "1d": "1D",
  "1w": "1W",
  "1m": "1M",
  all: "ALL",
};

/** Series colours (design 59): a colour names a series, never a sign. */
const INK = "#15191b";
const POS = "#0b8a66";
const TIER = "#6a4fc4";
const FREE = "#c9c5ba";
const SURFACE = "#ffffff";
const TRANSPARENT = "rgba(0, 0, 0, 0)";
const RESULT_LINES: Record<ResultSeries, { color: string; marker: string }> = {
  total: { color: INK, marker: INK },
  // Width is an integer in lightweight-charts (1–4), so the mockup's thinner lines become 2 px
  // at ~0.9 alpha.
  pools: { color: "rgba(11, 138, 102, 0.9)", marker: POS },
  spot: { color: "rgba(106, 79, 196, 0.9)", marker: TIER },
};
/** Value fills: each series colour blended 50 % over white, opaque, so stacked bands never mix. */
const VALUE_LAYERS: Record<ValueSeries, { fill: string; marker: string }> = {
  pools: { fill: "#85c5b3", marker: POS },
  spot: { fill: "#b4a7e1", marker: TIER },
  freeCash: { fill: "#e4e2dc", marker: FREE },
};
const CHIP_BOXES: Record<ChartMode, Record<string, string>> = {
  result: { total: INK, pools: POS, spot: TIER, freeCash: FREE },
  value: { pools: "#19b588", spot: TIER, freeCash: FREE },
};
const BREAK_EVEN = "rgba(194, 69, 58, 0.55)";
/** Value mode: the stack's floor is zero, so the price scale starts there. */
const fromZero: AutoscaleInfoProvider = (original) => {
  const info = original();
  if (info?.priceRange) info.priceRange.minValue = 0;
  return info;
};

function themeColor(name: string, fallback: string) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}

/** Axis ticks only: the figures in the header are the server's own strings, never these. */
const axisUsd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});
const axisPercent = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });
function axisLabel(unit: ChartUnit) {
  return (value: number) =>
    unit === "usd" ? axisUsd.format(value || 0) : `${axisPercent.format(value || 0)}%`;
}

/** Time-axis ticks in local time (lightweight-charts formats them in UTC by default). */
function tickLabel(time: Time, type: TickMarkType) {
  const date = new Date(Number(time) * 1000);
  if (type === TickMarkType.Time || type === TickMarkType.TimeWithSeconds)
    return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
  return formatPlotDate(date.toISOString());
}

function useCompact(): boolean {
  const query = "(max-width: 760px)";
  const [compact, setCompact] = useState(() => {
    // jsdom and embedded views have no matchMedia (same pattern as AutopilotCandidate.tsx).
    const matchMedia = window.matchMedia as typeof window.matchMedia | undefined;
    return matchMedia ? matchMedia.call(window, query).matches : false;
  });
  useEffect(() => {
    const matchMedia = window.matchMedia as typeof window.matchMedia | undefined;
    if (!matchMedia) return;
    const media = matchMedia.call(window, query);
    setCompact(media.matches);
    const update = (event: MediaQueryListEvent) => {
      setCompact(event.matches);
    };
    media.addEventListener("change", update);
    return () => {
      media.removeEventListener("change", update);
    };
  }, []);
  return compact;
}

const asTime = <S,>(rows: PlotRow<S>[]) =>
  rows.map((row) => ({ ...row, time: row.time as UTCTimestamp }));

interface PlotProps {
  points: PortfolioPoint[];
  mode: ChartMode;
  unit: ChartUnit;
  /** Checked, drawable series of the current mode. */
  drawn: readonly string[];
  compact: boolean;
  onHover: (point: PortfolioPoint | null) => void;
}

/** The lightweight-charts canvas: lines in Result mode, opaque stacked areas in Value mode. */
function PortfolioPlot({ points, mode, unit, drawn, compact, onHover }: PlotProps) {
  const container = useRef<HTMLDivElement | null>(null);
  const chart = useRef<IChartApi | null>(null);
  const series = useRef<ISeriesApi<SeriesType>[]>([]);
  const byTime = useRef(new Map<number, PortfolioPoint>());
  const hover = useRef(onHover);
  hover.current = onHover;

  useEffect(() => {
    const node = container.current;
    if (!node) return;
    const faint = themeColor("--faint", "#8b9194");
    const next = createChart(node, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: SURFACE },
        textColor: faint,
        fontFamily: themeColor("--font-mono", '"Geist Mono", monospace'),
        fontSize: 10,
        attributionLogo: false,
      },
      grid: {
        vertLines: { visible: false },
        horzLines: { color: themeColor("--line", "#efede7") },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: faint, width: 1, style: LineStyle.Dashed, labelVisible: false },
        horzLine: { visible: false, labelVisible: false },
      },
      // Touch: press and drag moves the figure; nothing scrolls or zooms the chart away.
      handleScroll: false,
      handleScale: false,
      trackingMode: { exitMode: TrackingModeExitMode.OnTouchEnd },
      rightPriceScale: { visible: false },
      leftPriceScale: { visible: true, borderVisible: false },
      timeScale: {
        borderVisible: false,
        fixLeftEdge: true,
        fixRightEdge: true,
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: tickLabel,
      },
    });
    const move = (event: MouseEventParams) => {
      if (event.time === undefined || !event.point) {
        hover.current(null);
        return;
      }
      hover.current(byTime.current.get(Number(event.time)) ?? null);
    };
    next.subscribeCrosshairMove(move);
    chart.current = next;
    return () => {
      next.unsubscribeCrosshairMove(move);
      next.remove();
      chart.current = null;
      series.current = [];
      // The chart is the only source of hover-leave events: a hover never outlives its chart.
      hover.current(null);
    };
  }, []);

  const drawnKey = drawn.join(",");
  useEffect(() => {
    const active = chart.current;
    if (!active) return;
    for (const old of series.current) active.removeSeries(old);
    series.current = [];
    byTime.current = new Map(points.map((point) => [plotTime(point.at), point]));
    const value = mode === "value";
    active.applyOptions({
      localization: { priceFormatter: axisLabel(unit) },
      leftPriceScale: {
        visible: !compact,
        // Value starts at zero (the stack's floor); Result keeps room around break-even.
        scaleMargins: value ? { top: 0.08, bottom: 0 } : { top: 0.1, bottom: 0.1 },
      },
      timeScale: { visible: !compact },
    });
    const common = {
      priceScaleId: "left",
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerRadius: 3.5,
      crosshairMarkerBorderColor: SURFACE,
      crosshairMarkerBorderWidth: 1.5,
    };
    if (value) {
      // Back to front: total edge, pools+spot edge, pools edge — the server's own sums. An
      // unchecked layer is painted with the panel surface, so the others keep their place.
      const layers: ValueSeries[] = ["freeCash", "spot", "pools"];
      for (const layer of layers) {
        const on = drawn.includes(layer);
        const fill = on ? VALUE_LAYERS[layer].fill : SURFACE;
        const area = active.addSeries(AreaSeries, {
          ...common,
          lineColor: fill,
          topColor: fill,
          bottomColor: fill,
          lineWidth: 1,
          crosshairMarkerVisible: on,
          crosshairMarkerBackgroundColor: VALUE_LAYERS[layer].marker,
          autoscaleInfoProvider: fromZero,
        });
        area.setData(
          asTime(
            plotRows(points, (point) => valueEdge(point, layer), {
              lineColor: TRANSPARENT,
              topColor: TRANSPARENT,
              bottomColor: TRANSPARENT,
            }),
          ),
        );
        series.current.push(area);
      }
    } else {
      const zero = active.addSeries(LineSeries, {
        ...common,
        color: BREAK_EVEN,
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        crosshairMarkerVisible: false,
      });
      zero.setData(points.map((point) => ({ time: plotTime(point.at) as UTCTimestamp, value: 0 })));
      series.current.push(zero);
      const order: ResultSeries[] = ["spot", "pools", "total"];
      for (const key of order.filter((candidate) => drawn.includes(candidate))) {
        const line = active.addSeries(LineSeries, {
          ...common,
          color: RESULT_LINES[key].color,
          lineWidth: 2,
          crosshairMarkerBackgroundColor: RESULT_LINES[key].marker,
        });
        line.setData(
          asTime(
            plotRows(points, (point) => resultFigure(point, key, unit), { color: TRANSPARENT }),
          ),
        );
        series.current.push(line);
      }
    }
    active.timeScale().fitContent();
    // drawnKey stands for `drawn`, whose identity changes on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, mode, unit, drawnKey, compact]);

  return (
    <>
      <div className="portfolio-canvas" ref={container} data-testid="portfolio-canvas" />
      <a
        href="https://www.tradingview.com/"
        target="_blank"
        rel="noreferrer"
        className="chart-attribution"
      >
        Charts by TradingView
      </a>
    </>
  );
}

function usd(value: string | null | undefined) {
  return value === null || value === undefined ? "—" : formatMoney(value);
}

function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
  disabled,
}: {
  label: string;
  options: readonly { value: T; label: string }[];
  value: T | null;
  onChange: (value: T) => void;
  disabled?: (value: T) => boolean;
}) {
  return (
    <div className="portfolio-seg" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          type="button"
          key={option.value}
          className={value === option.value ? "on" : undefined}
          aria-pressed={value === option.value}
          disabled={disabled?.(option.value) ?? false}
          onClick={() => {
            onChange(option.value);
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export interface ChartStaleness {
  total: boolean;
  pools: string | null;
  spot: string | null;
  /** The balance read behind today's Value figures is a retained one (as the Total pill's marker). */
  value: boolean;
}

interface Readout {
  big: string;
  tone: string;
  bigStale: string | null;
  parts: { key: "pools" | "spot"; color: string; figure: string; stale: string | null }[];
}

function money(unit: ChartUnit, result: { result: string | null; percent: string | null } | null) {
  if (!result) return { figure: "—", raw: null };
  const raw = unit === "usd" ? result.result : result.percent;
  return { figure: unit === "usd" ? formatSignedAmount(raw) : formatPercent2(raw), raw };
}

export interface PortfolioChartProps {
  id: string;
  preferences: ChartPreferences;
  onPreferences: (next: ChartPreferences) => void;
  /** The row's live results (USD; percent of invested from the server) for "today". */
  results: TradingResults | null;
  /** The row's live balance split (USD) for "today" in Value mode. */
  split: BalanceSplit | null;
  /** Whether the autopilot has a spot leg. */
  spotLeg: boolean;
  /** The row's own stale markers for today's results. */
  stale: ChartStaleness;
}

/**
 * The portfolio chart panel under the summary row (design 59): Result | Value, $ | %, 1D 1W 1M
 * ALL; a large figure that follows the crosshair with pools and spot beside it; series as
 * checkboxes. Every figure is a server USD (or percent) string printed with the shared helpers.
 */
export function PortfolioChart({
  id,
  preferences,
  onPreferences,
  results,
  split,
  spotLeg,
  stale,
}: PortfolioChartProps) {
  const compact = useCompact();
  const [modesOpen, setModesOpen] = useState(false);
  const [series, setSeries] = useState<PortfolioSeries | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [reload, setReload] = useState(0);
  const [hoveredPoint, setHovered] = useState<PortfolioPoint | null>(null);
  const mode = preferences.mode;
  const unit: ChartUnit = mode === "value" ? "usd" : preferences.unit;
  const effective = series ? effectiveRange(preferences.range, series.available) : null;
  const target = effective ?? preferences.range;

  useEffect(() => {
    const timer = window.setInterval(() => {
      setReload((value) => value + 1);
    }, REFRESH_EVERY_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setFailed(false);
    api<PortfolioSeries>(`/portfolio/series?range=${target}`, { signal: controller.signal }).then(
      (next) => {
        if (controller.signal.aborted) return;
        setSeries(next);
        setLoading(false);
      },
      () => {
        if (controller.signal.aborted) return;
        setFailed(true);
        setLoading(false);
      },
    );
    return () => {
      controller.abort();
    };
  }, [target, reload]);

  const data = series?.range === target ? series : null;
  // A hovered point belongs to the plot on screen; once that plot is gone, today's figures return.
  const hovered = data !== null && !failed ? hoveredPoint : null;
  const points = useMemo(() => data?.points ?? [], [data]);
  const empty = data !== null && effectiveRange(preferences.range, data.available) === null;
  const chips = chipStates(points, mode, unit, spotLeg);
  const checked: readonly string[] = mode === "result" ? preferences.result : preferences.value;
  const drawn = chips
    .filter((chip) => chip.disabledReason === null && checked.includes(chip.key))
    .map((chip) => chip.key);
  const now = Date.now();

  const shown = (key: "pools" | "spot") =>
    checked.includes(key) && chips.some((chip) => chip.key === key);
  const readout: Readout = (() => {
    if (mode === "value") {
      const point = hovered;
      const pools = point ? (point.value?.pools ?? null) : (split?.inPools.usd ?? null);
      const spot = point ? (point.value?.spot ?? null) : (split?.inSpot?.usd ?? null);
      return {
        big: usd(point ? (point.value?.total ?? null) : (split?.total.usd ?? null)),
        tone: "",
        // Today's figures are the balance line's own; a retained read says so, as the Total pill does.
        bigStale: !point && stale.value ? "stale" : null,
        parts: [
          ...(shown("pools")
            ? [{ key: "pools" as const, color: POS, figure: usd(pools), stale: null }]
            : []),
          ...(shown("spot")
            ? [{ key: "spot" as const, color: TIER, figure: usd(spot), stale: null }]
            : []),
        ],
      };
    }
    const source = hovered ? hovered.result : results;
    const mark = (flag: boolean | undefined) => (flag ? "stale" : null);
    const total = money(unit, source?.total ?? null);
    const pools = money(unit, source?.pools ?? null);
    const spot = money(unit, source?.spot ?? null);
    return {
      big: total.figure,
      tone: total.raw === null ? "" : signTone(total.raw),
      bigStale: hovered ? mark(hovered.result?.total.stale) : stale.total ? "stale" : null,
      parts: [
        ...(shown("pools")
          ? [
              {
                key: "pools" as const,
                color: POS,
                figure: pools.figure,
                stale: hovered ? mark(hovered.result?.pools.stale) : stale.pools,
              },
            ]
          : []),
        ...(shown("spot")
          ? [
              {
                key: "spot" as const,
                color: TIER,
                figure: spot.figure,
                stale: hovered ? mark(hovered.result?.spot?.stale) : stale.spot,
              },
            ]
          : []),
      ],
    };
  })();

  const label = hovered
    ? hoverLabel(mode, hovered)
    : intervalLabel(mode, data?.firstRecordedDay ?? null, target, now);
  const note = data ? chartNote(mode, data, now, data.points) : null;
  const young = data ? youngRecord(data, now) && !data.points.some((p) => p.reconstructed) : false;

  const toggle = (chip: ChipState) => {
    setHovered(null);
    if (mode === "result") {
      const key = chip.key as ResultSeries;
      const next = preferences.result.includes(key)
        ? preferences.result.filter((item) => item !== key)
        : [...preferences.result, key];
      onPreferences({ ...preferences, result: next });
    } else {
      const key = chip.key as ValueSeries;
      const next = preferences.value.includes(key)
        ? preferences.value.filter((item) => item !== key)
        : [...preferences.value, key];
      onPreferences({ ...preferences, value: next });
    }
  };

  const modes = (
    <div className="portfolio-modes">
      <Segmented<ChartMode>
        label="Chart mode"
        options={[
          { value: "result", label: "Result" },
          { value: "value", label: "Value" },
        ]}
        value={mode}
        onChange={(next) => {
          setHovered(null);
          onPreferences({ ...preferences, mode: next });
        }}
      />
      <Segmented<ChartUnit>
        label="Chart unit"
        options={[
          { value: "usd", label: "$" },
          { value: "percent", label: "%" },
        ]}
        value={unit}
        disabled={(value) => value === "percent" && mode === "value"}
        onChange={(next) => {
          setHovered(null);
          // The pressed button (Value's forced $ included) never overwrites the remembered unit.
          if (next === unit) return;
          onPreferences({ ...preferences, unit: next });
        }}
      />
    </div>
  );

  return (
    <section
      id={id}
      className={`portfolio-panel${modesOpen ? " modes-open" : ""}${young ? " young" : ""}`}
      aria-label="Portfolio chart"
    >
      <div className="portfolio-head">
        <div className="portfolio-label-line">
          <span className="portfolio-label">{label}</span>
          <button
            type="button"
            className="portfolio-mode-button"
            aria-expanded={modesOpen}
            onClick={() => {
              setModesOpen((open) => !open);
            }}
          >
            {mode === "result" ? "Result" : "Value"} · {unit === "usd" ? "$" : "%"}{" "}
            <svg className="balance-chevron" viewBox="0 0 14 14" aria-hidden="true">
              <path d={modesOpen ? "m3.5 8.5 3.5-3.5 3.5 3.5" : "m3.5 5.5 3.5 3.5 3.5-3.5"} />
            </svg>
          </button>
        </div>
        <div className="portfolio-figures">
          <span
            className={`num portfolio-figure${readout.tone ? ` ${readout.tone}` : ""}`}
            data-testid="portfolio-figure"
          >
            {readout.big}
          </span>
          {readout.bigStale !== null && <small className="summary-stale">{readout.bigStale}</small>}
          {readout.parts.map((part) => (
            <span key={part.key} className="num portfolio-part">
              <span style={{ color: part.color }} aria-hidden="true">
                ●
              </span>{" "}
              {part.key} {part.figure}
              {part.stale !== null && <small className="summary-stale"> {part.stale}</small>}
            </span>
          ))}
        </div>
        {modes}
        <Segmented<PortfolioRange>
          label="Chart interval"
          options={PORTFOLIO_RANGES.map((range) => ({ value: range, label: RANGE_LABELS[range] }))}
          value={target}
          disabled={(range) => (series ? !series.available[range] : false)}
          onChange={(range) => {
            setHovered(null);
            onPreferences({ ...preferences, range });
          }}
        />
      </div>
      <div className="portfolio-chips">
        {chips.map((chip) => {
          const on = chip.disabledReason === null && checked.includes(chip.key);
          return (
            <button
              type="button"
              role="checkbox"
              key={chip.key}
              className={`portfolio-chip${on ? "" : " off"}${
                mode === "result" && chip.key === "freeCash" ? " value-only" : ""
              }`}
              aria-checked={on}
              disabled={chip.disabledReason !== null}
              onClick={() => {
                toggle(chip);
              }}
            >
              <span
                className="portfolio-chip-box"
                style={{
                  background: chip.disabledReason === null ? CHIP_BOXES[mode][chip.key] : FREE,
                }}
                aria-hidden="true"
              />
              {chip.disabledReason ?? chip.label}
            </button>
          );
        })}
        {note !== null && <span className={`portfolio-note${young ? " young" : ""}`}>{note}</span>}
      </div>
      <div className="portfolio-area">
        {failed ? (
          <div className="portfolio-message" role="alert">
            Couldn&apos;t load the chart
            <button
              type="button"
              className="button small"
              onClick={() => {
                setReload((value) => value + 1);
              }}
            >
              Retry
            </button>
          </div>
        ) : data === null ? (
          loading && (
            <div className="portfolio-skeleton" role="status" aria-label="Loading the chart" />
          )
        ) : empty ? (
          <div className="portfolio-message">
            No history yet · a point is recorded every 10 minutes
          </div>
        ) : (
          <PortfolioPlot
            points={points}
            mode={mode}
            unit={unit}
            drawn={drawn}
            compact={compact}
            onHover={setHovered}
          />
        )}
      </div>
      {!empty && points.length > 0 && (
        <div className="portfolio-edges num" aria-hidden="true">
          <span>{formatPlotDate(points[0]?.at ?? "")}</span>
          <span>{formatPlotDate(points.at(-1)?.at ?? "")}</span>
        </div>
      )}
    </section>
  );
}

/** The summary row's `Chart ⌄` button (design 59), left of `Open Autopilot →`. */
export function PortfolioChartButton({
  open,
  panelId,
  onToggle,
}: {
  open: boolean;
  panelId: string;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      className={`button ghost small summary-row-chart${open ? " open" : ""}`}
      aria-expanded={open}
      aria-controls={panelId}
      onClick={onToggle}
    >
      <svg width="14" height="12" viewBox="0 0 14 12" fill="none" aria-hidden="true">
        <path
          d="M1 9 L4 5 L7 7 L13 1"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      Chart
      <svg
        className="balance-chevron summary-row-chart-chevron"
        viewBox="0 0 14 14"
        aria-hidden="true"
      >
        <path d={open ? "m3.5 8.5 3.5-3.5 3.5 3.5" : "m3.5 5.5 3.5 3.5 3.5-3.5"} />
      </svg>
    </button>
  );
}
